import React, { useEffect, useRef } from "react";
import {
  isOverInteractive,
  spawnParticles,
  stepAndRender,
} from "./particle/particleHelpers";

/**
 * Lightweight golden particle cursor trail.
 * - Disabled on coarse-pointer (touch) devices
 * - Respects prefers-reduced-motion (skips particle spawning)
 * - Dims intensity over interactive elements
 *
 * The heavy lifting (spawn / step / draw) lives in particleHelpers so this
 * component stays small and easy to reason about.
 */
const ParticleCursor = () => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: -100, y: -100, prevX: -100, prevY: -100 });
  const overInteractiveRef = useRef(false);
  const rafRef = useRef(0);

  useEffect(() => {
    const canFine = window.matchMedia("(pointer: fine)").matches;
    if (!canFine) return undefined;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // The render loop idles itself when there is nothing to animate
    // (no live particles, cursor at rest). A permanently running rAF on a
    // full-viewport blended canvas forces the compositor to re-blend the
    // whole page every frame, which makes scrolling feel heavy.
    let running = false;
    let lastX = -1;
    let lastY = -1;

    const tick = () => {
      const mouse = mouseRef.current;
      stepAndRender(ctx, canvas, particlesRef.current, mouse, overInteractiveRef.current);
      const idle =
        particlesRef.current.length === 0 && mouse.x === lastX && mouse.y === lastY;
      lastX = mouse.x;
      lastY = mouse.y;
      if (idle) {
        running = false;
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const wake = () => {
      if (running) return;
      running = true;
      rafRef.current = requestAnimationFrame(tick);
    };

    const onMove = (e) => {
      const mouse = mouseRef.current;
      mouse.prevX = mouse.x;
      mouse.prevY = mouse.y;
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      overInteractiveRef.current = isOverInteractive(e.target);
      if (!reduceMotion) {
        spawnParticles(particlesRef.current, mouse, overInteractiveRef.current);
      }
      wake();
    };
    window.addEventListener("mousemove", onMove);
    wake();

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="particle-cursor-canvas"
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 60,
        mixBlendMode: "screen",
      }}
    />
  );
};

export default ParticleCursor;
